import React, { useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { KnoxSession } from "../api/auth/twoFactorClient.ts";

type AuthContextValue = {
  session: KnoxSession | null;
  completeKnoxLogin: (payload: KnoxSession, redirectPath?: string) => void;
  logOutAction: () => void;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{
  children: ReactNode[] | ReactNode;
}> = ({ children }) => {
  const [session, setSession] = useState<KnoxSession | null>(() => {
    const savedSession = localStorage.getItem("session");
    return savedSession ? (JSON.parse(savedSession) as KnoxSession) : null;
  });
  const navigate = useNavigate();
  useEffect(() => {
    if (session) {
      localStorage.setItem("session", JSON.stringify(session));
    } else {
      localStorage.removeItem("session");
    }
  }, [session]);

  const completeKnoxLogin = useCallback((payload: KnoxSession, redirectPath?: string) => {
    setSession(payload);
    navigate(redirectPath || "/");
  }, [navigate]);

  const logOutAction = useCallback(() => {
    setSession(null);
    localStorage.removeItem("session");
    navigate("/login");
  }, [navigate]);

  const value = {
    session,
    completeKnoxLogin,
    logOutAction,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
};
